import { useEffect, useRef, useState } from "react";
import type { RefineBatchInput, RefineBatchResult, RefineUploadProvider } from "@studio-features/meta-crew/library-refine-upload";
import type { RefineView } from "@studio-features/meta-crew/refine-data";
import { api, errorMessage } from "./api";
import { can } from "./access";
import { useCodexStatus } from "./codex-api";
import { useAiModels } from "./llm-api";
import { useGuidelines, useRefineInvalidate } from "./refine-api";
import { refineUploadQueue } from "./upload-queue";
import { HQ_SCOPE, uploadAdminFile } from "./library-adapter";
import type { Me } from "./types";

/**
 * 원고함 "정형화해서 올리기" 채널 provider (요청서 31절, 설계 14-1). 원고함과 정형화 탭이 같은 공통 업로드 창(시안 LibraryUpload)을 쓰므로
 * 가이드라인·모델·준비 상태·파일 올리기·패키지 만들기(`POST /admin/manuscripts/refine/batch`)를 한 곳에서 묶는다.
 * 본문 고르기·보조 문서·지원하지 않는 파일 판정은 서버가 한다. 화면은 항목별 {ok, id, error} 를 그대로 보인다.
 */
type BatchResponse = { items: { path: string; ok: boolean; id?: string; title?: string; code?: string; error?: string }[]; created: number; failed: number };

export function useRefineUploadProvider(me: Me, hooks: { onOpenList?: (ids: string[]) => void; onOpenSettings?: () => void; /** 트레이 묶음 이름용 */ customerName?: (id: string) => string | undefined }) {
  const canRead = can(me, "MANUSCRIPT_READ");
  const guidelines = useGuidelines(canRead);
  const apiModels = useAiModels(canRead);
  // 실행 방식이 GPT 플랜이면 실행기 계정의 모델 목록 (SYSTEM_READ 가 없으면 API 키 모델로 보인다 — 서버가 실제 모델을 정한다)
  const codex = useCodexStatus(can(me, "SYSTEM_READ"));
  const invalidate = useRefineInvalidate();
  // 백그라운드 큐가 작업을 만들면 목록을 다시 읽는다 (요청서 34절). invalidate 는 렌더마다 새 함수라 ref 로 한 번만 구독
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  useEffect(() => refineUploadQueue.onCreated(() => void invalidateRef.current()), []);
  const [readiness, setReadiness] = useState<RefineView["readiness"]>();
  const usePlan = codex.data?.provider === "codex";
  const models: RefineView["models"] = usePlan
    ? { billing: "plan", default: codex.data!.model || codex.data!.defaultModel || "", allowed: codex.data!.models.map((m) => m.slug) }
    : { default: apiModels.data?.default ?? "", allowed: apiModels.data?.allowed ?? [], prices: apiModels.data?.prices };

  /** 키 없음·실행기 연결 안 됨·예산 초과는 올리기 창의 안내 칸에도 보인다 (설정 바로가기). 그 밖은 문구 그대로 던진다 */
  const fail = (e: unknown): never => {
    const msg = errorMessage(e);
    if (/^LLM_NOT_READY/.test(msg) || /^REFINE_BUDGET/.test(msg)) {
      const m = /\$([\d.]+)[^$]*\$([\d.]+)/.exec(msg);
      setReadiness({ reason: msg.replace(/^(LLM_NOT_READY|REFINE_BUDGET):\s*/, ""), ...(m ? { usedUsd: Number(m[1]), limitUsd: Number(m[2]) } : {}) });
    }
    throw new Error(msg);
  };
  // 정형화는 관리자 업무 → 파일은 관리자 파일 (요청서 35-1, 원고 소유 설계 3-2). 고객사 값은 받지 않는다
  const uploadFile = async (_customerId: string, file: File) => {
    try {
      return await uploadAdminFile(file);
    } catch (e) {
      throw new Error(errorMessage(e));
    }
  };
  const batch = async (input: RefineBatchInput): Promise<RefineBatchResult> => {
    try {
      const r = await api.post<BatchResponse>("/admin/manuscripts/refine/batch", {
        customerId: input.customerId && input.customerId !== HQ_SCOPE ? input.customerId : undefined,
        folderId: input.folderId || undefined,
        guidelineId: input.guidelineId || undefined,
        model: input.model || undefined,
        instructions: input.instructions?.trim() || undefined,
        items: input.items.map((it) => ({ path: it.path, title: it.title?.trim() || undefined, texts: it.texts, files: it.files })),
      });
      setReadiness(undefined);
      await invalidate();
      // 실패 문구는 서버 것을 그대로. code 만 있고 error 가 없으면 code 를 보인다
      return { items: r.items.map((it) => ({ path: it.path, ok: it.ok, id: it.id, error: it.error ?? (it.ok ? undefined : it.code) })) };
    } catch (e) {
      return fail(e);
    }
  };
  const provider: RefineUploadProvider = {
    guidelines: guidelines.versions,
    models,
    canCreate: can(me, "MANUSCRIPT_REFINE"),
    // 파일 귀속 범위 = 관리자 (화면에는 안 보임). 시안이 enqueue/batch 의 customerId 로 이 값을 실어 보낸다
    hqCustomerId: HQ_SCOPE,
    loading: guidelines.loading,
    error: guidelines.error ? errorMessage(guidelines.error) : undefined,
    readiness,
    uploadFile,
    batch,
    // 요청서 34절: 올리기 창은 선택만 넘기고 닫힌다. 업로드·작업 만들기는 전역 큐(트레이)가 이어서 한다
    enqueue: (job) => refineUploadQueue.enqueue(job, hooks.customerName?.(job.customerId)),
    onOpenList: hooks.onOpenList,
    onOpenSettings: hooks.onOpenSettings,
  };
  const clearReadiness = () => setReadiness(undefined);
  return { provider, models, guidelines: guidelines.versions, readiness, fail, clearReadiness, uploadFile };
}

